/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import classnames from 'classnames';
import {omitBy} from 'lodash';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SCIField, SCIPendingSettings} from '@ion/core/src/types/entities';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {BulkSCIAdvanceTab} from './BulkSCIAdvanceTab';
import {BulkSCICustomTab} from './BulkSCICustomTab';
import {BulkSCIWifiTab} from './BulkSCIWifiTab';
import {SCIReportsTab} from './SCIReportsTab';
import {SCISensorsTab} from './SCISensorsTab';
import {getDefaultFieldsObj} from './SCISettingFieldMapping';
import {
  convertLoggingPeriodInterval,
  getBulkSelectedValue,
  getDisableGPSValue,
  getSensorLoggingPeriodTabIndex,
  isSensorField
} from './SCISettingsUtils';

// defines the enumeration for SCI tabs
enum BulkSCITab {
  CONTROLS = 0,
  REPORTS = 1,
  SENSORS = 2,
  WIFI = 3,
  ADVANCE = 4,
};

export interface BulkSCISettingsProps extends BulkSCIStyleProps {
  selectedDevicetype: string;
  setChangedSettingFields: Function;
  setBulkSettingPersist?: Function;
  bulkSettingPersist?: SCIPendingSettings;
};

export interface BulkSCIStyleProps {
  isBulkAction?: boolean;
};

export const styleOverrides = (theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display : 'flex'
  },
  leftContainer : {
    flex        : 1,
    marginTop   : '21px',
    marginRight : '10px',
    padding     : '20px',
    width       : '30%'
  },
  rightContainer : {
    width         : '75%',
    borderRradius : '5px',
    marginTop     : '21px',
    padding       : '0px 10px',
    height        : '430px',
    overflowY     : 'auto'
  },
  content : {
    padding : '30px 0 0 20px'
  },
  itemStyle : {
    ...dlsAlt('typography', 'label1'),
    color        : dlsColor('grey2'),
    marginBottom : '20px'
  },
  idReportColor : {
    borderBottom : `1px solid ${dlsColor('aaBlue')}`,
    color        : dlsColor('black')
  },
  paperStyle : {
    display         : 'inline-block',
    boxShadow       : 'none',
    backgroundColor : 'transparent !important'
  },
  customTabPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '91%',
    boxShadow : 'none',
    display   : 'flex'
  },
  radioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    paddingLeft   : '65%',
    wordSpacing   : '8px',
    letterSpacing : '0px'
  },
  noDataStyle : {
    padding : '18% 0 0 27%'
  },
  idReportPaper : {
    marginTop : '20px',
    width     : '745px'
  },
  reportPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    boxShadow : 'none',
    display   : 'flex'
  },
  reportsFields : {
    width : '33%'
  },
  sensorRadioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    padding       : '0 0 2px 50%',
    wordSpacing   : '3px',
    letterSpacing : '0px'
  },
  sensorFiels : {
    marginTop : '13px',
    overflow  : 'auto',
    width     : '91%',
    boxShadow : 'none',
    display   : 'flex'
  },
  sensorFormFields : {
    width           : '91%',
    backgroundColor : 'white'
  },
  sensorSubFormWidth : {
    width : '153%'
  },
  addBtn : {
    marginTop : '24px'
  },
  gridStyle : {
    paddingTop : '10px',
    width      : '94%'
  },
  advanceTabPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '100%',
    boxShadow : 'none',
    display   : 'flex'
  },
  sciSettingLabel : {
    ...dlsAlt('typography', 'cta3'),
    paddingLeft : '25px',
    fontWeight  : 600,
    color       : dlsColor('grey1')
  },
  sensorTemperatureFiels : {
    marginTop : '13px',
    width     : '91%',
    boxShadow : 'none',
    display   : 'flex'
  },
  advancedRadioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    padding       : '5px 0 0 54%',
    wordSpacing   : '3px',
    letterSpacing : '0px'
  },
  bulkRadioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    paddingLeft   : '54%',
    paddingBottom : '10px',
    paddingTop    : '10px',
    letterSpacing : '0px',
    wordSpacing   : '38px'
  },
  bulkAdvancedRadioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    padding       : '0 0 0 50%',
    letterSpacing : '0px',
    wordSpacing   : '38px'
  },
  bulkSensorRadioLabel : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey2'),
    fontSize      : '15px',
    padding       : '0 0 2px 35%',
    letterSpacing : '0px',
    wordSpacing   : '38px'
  },
  bulkIdReportPaper : {
    marginTop : '20px',
    width     : '157%'
  },
  bulkFlexStyle : {
    display   : 'flex',
    width     : '165%',
    marginTop : '10px'
  },
  bulkGridStyle : {
    paddingTop : '10px',
    width      : '165%'
  },
  bulkAdvanceTabPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    boxShadow : 'none',
    display   : 'flex'
  },
  bulkSensorFormFields : {
    width           : '100%',
    backgroundColor : 'white'
  },
  bulkActionsTile : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : 'white',
    padding         : 10,
    alignItems      : 'center',
    marginTop       : '8px'
  }
});

/**
 * Edit SCI device settings in bulk
 */
export function BulkSCISettings({
  selectedDevicetype,
  setChangedSettingFields,
  setBulkSettingPersist,
  bulkSettingPersist,
  isBulkAction = false
}: BulkSCISettingsProps) {
  const editorStyles  = useExtendStyles(styles, styleOverrides);
  const classes       = editorStyles();
  const [customInitialFields,
    reportAndLogsInitialFields,
    sensorsInitialFields,
    advanceInitialFields,
    wifi,
    initialObjMeta] = getDefaultFieldsObj(bulkSettingPersist, selectedDevicetype);

  const [sciTabValue, setSciTabValue]           = useState<number>(0);
  const [customTabFields, setCustomTabFields]   = useState(customInitialFields);
  const [reportTabFields, setReportTabFields]   = useState(reportAndLogsInitialFields);
  const [sensorsTabFields, setSensorsTabFields] = useState(sensorsInitialFields);
  const [advanceTabFields, setAdvanceTabFields] = useState(advanceInitialFields);
  const [wifiNetworks, setWifiNetworks]         = useState(wifi);

  useEffect(() => {
    const customAndSensorsStates = [customTabFields, sensorsTabFields];
    const reportAndAdvanceStates = [reportTabFields, advanceTabFields];
    let extractedFields: SCIPendingSettings = {};
    let changedSettingsFields: GenericObject = {};

    const changedArray = [getChangedFields(customAndSensorsStates)];
    reportAndAdvanceStates.forEach((tab, indx) => changedArray.push(getChangedFields(tab)));
    extractedFields = Object.assign({}, ...changedArray);
    const initialFields = initialObjMeta;
    if (initialFields?.wifiNetworks) {
      extractedFields.wifiNetworks = wifiNetworks?.wifiNetworks;
    }

    extractedFields = omitBy(extractedFields, isUndefined);
    changedSettingsFields = updatedDiff(initialFields, extractedFields);

    if (changedSettingsFields?.wifiNetworks) {
      const networksArray = [];
      for (const [, value] of Object.entries(changedSettingsFields?.wifiNetworks.networks)) {
        networksArray.push(value);
      }
      if (networksArray.length) {
        const constructStructure = {networks: networksArray};
        changedSettingsFields.wifiNetworks = JSON.stringify(constructStructure);
      } else {
        delete changedSettingsFields.wifiNetworks;
      }
    }

    if (changedSettingsFields?.disableGPS !== undefined) {
      changedSettingsFields.disableGPS = getDisableGPSValue(changedSettingsFields?.disableGPS);
    }

    setChangedSettingFields(changedSettingsFields);
    return () => {
      setBulkSettingPersist(extractedFields);
    };
  }, [customTabFields, reportTabFields, sensorsTabFields, advanceTabFields, wifiNetworks]);

  const getChangedFields = (customAndSensorsStates): SCIPendingSettings => {
    const extractedFields: SCIPendingSettings = {};
    customAndSensorsStates.forEach(tab => {
      tab.forEach(function(item) {
        for (const [key, value] of Object.entries(item)) {
          if (key !== 'label') {
            extractedFields[key] = value;
          }
        }
      });
    });
    return extractedFields;
  };

  const updatedDiff = (arr1: SCIPendingSettings[], arr2: SCIPendingSettings) => {
    const diff = Object.keys(arr2).reduce((diff, key) => {
      if (arr1[key] === arr2[key]) return diff;
      return {
        ...diff,
        [key] : arr2[key]
      };
    }, {});
    return diff;
  };

  const SCISettingsArr = useMemo(() => {
    const {controls, reportAndLog, sensors, wifi, advance} = translate('admin-devices:view.sciSettings', {
      returnObjects : true
    });
    return [controls, reportAndLog, sensors, wifi, advance];
  }, []);

  const getSCIProperties = useCallback((e: ChangeEvent<{}> | any) => {
    const idReportText = e.target.innerText;
    const convertedSCIArr = SCISettingsArr.map((item) => item.toUpperCase());
    const index = convertedSCIArr.indexOf(idReportText);
    setSciTabValue(index);
  }, []);

  const extendedClasses = useMemo(() => (
    classnames(classes.itemStyle, classes.idReportColor)
  ), [classes]);

  const getClass = (value: number, index: number, classes) => {
    return value === index ? extendedClasses : classes.itemStyle;
  };

  function handleReportAndLogs(e: MouseEvent<HTMLInputElement>, field: SCIField, active: number) {
    const activeReportSubTab = reportTabFields[active];
    let convertedReportingVal;
    const comparatorLabel = field.label;
    const key = Object.keys(field)[1];
    if (isString(e)) {
      convertedReportingVal = convertLoggingPeriodInterval(e);
    } else if (isObject(e)) {
      const reportValue = (e?.target as HTMLInputElement)?.value;
      convertedReportingVal = comparatorLabel === translate('admin-devices:view.sciSettings.reportMovementImmediately')
        ? getBulkSelectedValue(reportValue)
        : reportValue;
    } else {
      convertedReportingVal = e;
    }
    const changedArray = activeReportSubTab.map(field =>
      field.label === comparatorLabel
        ? {...field, [key]: convertedReportingVal}
        : field
    );
    const initialReportState = reportTabFields;
    initialReportState[active] = changedArray;
    setReportTabFields([...initialReportState]);
  };

  function handleSensorsFieldChange(e: MouseEvent<HTMLInputElement>, label: string, key: string) {
    let initialControlData = sensorsTabFields;
    let convertedValue;
    if (isString(e)) {
      convertedValue = getSensorLoggingPeriodTabIndex(e);
    } else if (isObject(e)) {
      convertedValue = (e?.target as HTMLInputElement)?.value;
    } else {
      convertedValue = e;
    }
    if (isSensorField(key)) {
      convertedValue =  getBulkSelectedValue((e?.target as HTMLInputElement)?.value);
    }
    const changedArray = initialControlData.map(field =>
      field.label === label
        ? {...field, [key]: convertedValue}
        : field
    );
    initialControlData = changedArray;
    setSensorsTabFields([...initialControlData]);
  };

  const getIsActive = function(value): boolean {
    return value;
  };

  const isRadioField = useCallback(({label}): boolean => {
    const {stationaryReportingdelay, loggingPeriod} = translate('admin-devices:view.sciSettings', {returnObjects: true});
    return label !== stationaryReportingdelay && label !== loggingPeriod;
  }, []);

  // Handles when the advance tab changes.
  function handleAdvanceFieldChange(e: MouseEvent<HTMLInputElement>, field: SCIField, active: number) {
    const initialAdvanceData = advanceTabFields;
    const activeAdvanceSubTab = advanceTabFields[active];
    let convertedValue;
    const comparatorLabel = field.label;
    if (isString(e)) {
      convertedValue = convertLoggingPeriodInterval(e);
    } else if (isObject(e)) {
      convertedValue = (e?.target as HTMLInputElement)?.value;
    } else {
      convertedValue = e;
    }
    if (isRadioField(field) && active !== 1) {
      convertedValue =  getBulkSelectedValue((e?.target as HTMLInputElement)?.value);
    }
    const key = Object.keys(field)[1];
    const changedArray = activeAdvanceSubTab.map(field =>
      field.label === comparatorLabel
        ? {...field, [key]: convertedValue}
        : field
    );
    initialAdvanceData[active] = changedArray;
    setAdvanceTabFields([...initialAdvanceData]);
  };

  return (
    <div className={!isBulkAction && classes.content}>
      {!isBulkAction && (
        <div className={classes.sciSettingLabel}>
          {translate('admin-devices:view.sciSettings.bulkSCILabel')}
        </div>
      )}
      <div className={classes.flexStyle}>
        <div className={classes.leftContainer}>
          <Paper className={classes.paperStyle}>
            <MenuList>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 0, classes)}>
                {SCISettingsArr[0]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 1, classes)}>
                {SCISettingsArr[1]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 2, classes)}>
                {SCISettingsArr[2]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 3, classes)}>
                {SCISettingsArr[3]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 4, classes)}>
                {SCISettingsArr[4]}
              </MenuItem>
            </MenuList>
          </Paper>
        </div>
        <div className={!isBulkAction && classes.rightContainer}>
          {sciTabValue === BulkSCITab.CONTROLS && (
            <BulkSCICustomTab selectedDevicetype={selectedDevicetype}
              customTabFields={customTabFields}
              setCustomTabFields={setCustomTabFields}
              isBulkAction={isBulkAction} />
          )}
          {sciTabValue === BulkSCITab.REPORTS && (
            <SCIReportsTab bulkSCI
              reportTabFields={reportTabFields}
              classes={classes}
              handleReportAndLogs={handleReportAndLogs}
              noUpdate={false}
              sciPendingSettingData={{}}
              getIsActive={getIsActive}
              initialReportAndLog={false}
              isBulkAction={isBulkAction} />
          )}
          {sciTabValue === BulkSCITab.SENSORS && (
            <SCISensorsTab bulkSCI
              deviceModel={selectedDevicetype}
              sensorsTabFields={sensorsTabFields}
              classes={classes}
              handleSensorsFieldChange={handleSensorsFieldChange}
              noUpdate={false}
              sciPendingSettingData={{}}
              getIsActive={getIsActive}
              isBulkAction={isBulkAction} />
          )}
          {sciTabValue === BulkSCITab.WIFI && (
            <BulkSCIWifiTab classes={classes}
              wifiNetworks={wifiNetworks}
              onSetWifiNetworks={setWifiNetworks}
              isBulkAction={isBulkAction} />
          )}
          {sciTabValue === BulkSCITab.ADVANCE && (
            <BulkSCIAdvanceTab deviceModel={selectedDevicetype}
              advanceTabFields={advanceTabFields}
              classes={classes}
              customTabFields={customTabFields}
              setCustomTabFields={setCustomTabFields}
              handleAdvanceFieldChange={handleAdvanceFieldChange}
              setAdvanceTabFields={setAdvanceTabFields}
              isBulkAction={isBulkAction} />
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkSCISettings;
